'use strict'

import * as React from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import 'react-datepicker/dist/react-datepicker.css'

import { useModal, useModalExtra } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import { ChrofyShowErrors } from '../../../common/Form'

import { validateFixCancelForm } from '../extra/validation'

registerLocale('ja', ja)

/**
 * @param {Date | null} d
 * @returns {string} YYYY/MM/DD または空
 */
function _toYyyyMmDd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return ''
  }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

/**
 * データ確定解除モーダル本文（§4.2②・§7.7.3・§8.1 / SR-040, SR-047〜050 UI）
 *
 * @param {{ onChange?: (form: { permitUser: string, permitDate: string }, setErrors: function): void }} props
 */
export function FixCancelModal(props) {
  const { onChange } = props ?? {}
  const [permitUser, setPermitUser] = React.useState('')
  const [permitDate, setPermitDate] = React.useState(() => new Date())
  /** @type {[Record<string, string>, React.Dispatch<React.SetStateAction<Record<string, string>>>]} */
  const [errors, setErrors] = React.useState({})

  React.useEffect(() => {
    onChange?.({ permitUser, permitDate: _toYyyyMmDd(permitDate) }, setErrors)
  }, [permitUser, permitDate, onChange])

  return (
    <div className="text-base">
      <div className="text-center pb-2">
        <h3 className="text-h5 mt-2 mb-2 font-bold">【許諾者情報】</h3>
      </div>
      <table className="form-table table-fixed w-full">
        <tbody>
          <tr>
            <th className="form-table__col-head w-[150px]">
              <label htmlFor="fixCancelPermitUser" className="text-h7 font-bold">
                許諾者名 <span className="red--text">*</span>
              </label>
            </th>
            <td className="form-table__col-data">
              <div className="form-control">
                <input
                  id="fixCancelPermitUser"
                  type="text"
                  className="text-input w-full text-left"
                  placeholder="許諾者名を入力"
                  value={permitUser}
                  onChange={(e) => setPermitUser(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <ChrofyShowErrors field="permitUser" errors={errors} />
            </td>
          </tr>
          <tr>
            <th className="form-table__col-head w-[150px]">
              <label htmlFor="fixCancelPermitDate" className="text-h7 font-bold">
                許諾日 <span className="red--text">*</span>
              </label>
            </th>
            <td className="form-table__col-data">
              <DatePicker
                id="fixCancelPermitDate"
                locale="ja"
                selected={permitDate}
                onChange={(d) => setPermitDate(d)}
                dateFormat="yyyy/MM/dd"
                placeholderText="YYYY/MM/DD"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className={`text-input rounded-lg text-center h-[40px] pr-[20px] w-[200px] text-[14px] font-normal${
                  errors.permitDate ? ' is-error' : ''
                }`}
              />
              <ChrofyShowErrors field="permitDate" errors={errors} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/**
 * データ確定解除確認モーダル（fix_cancel_data / SR-072〜074）
 *
 * @param {{
 *   globalStore: import('redux').Store
 *   operationUserSub: string
 *   operationUserName: string
 *   corporationNumber: string
 *   dataCreateTaskKey: string | number
 *   onExecute: (request: {
 *     operationUserSub: string
 *     operationUserName: string
 *     permitUserName: string
 *     permitDate: string
 *     key: { CorporationNumber: string; Key: string | number }
 *   }) => Promise<void>
 *   onAfterSuccess?: () => void | Promise<void>
 *   onFailure?: (error: unknown) => void
 * }} options
 */
export function useFixCancelModal(options) {
  const {
    globalStore,
    operationUserSub,
    operationUserName,
    corporationNumber,
    dataCreateTaskKey,
    onExecute,
    onAfterSuccess,
    onFailure
  } = options ?? {}

  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const { promisify } = useModifyClassList('.modal-window', ['w-[900px]'])

  const openFixCancelModal = React.useCallback(() => {
    return promisify((res) => {
      const componentRef = {
        /** @type {{ permitUser: string; permitDate: string } | null} */
        formDataRef: null,
        /** @type {React.Dispatch<React.SetStateAction<Record<string, string>>> | null} */
        setErrorsRef: null
      }

      const onChange = (formData, setErrors) => {
        componentRef.formDataRef = formData
        componentRef.setErrorsRef = setErrors
      }

      const description = (
        <ul className="text-body-2-medium text-left list-disc pl-6 max-w-xl mx-auto">
          <li>データの確定を解除します。よろしいですか？</li>
          <li>※許諾者情報を入力してください。</li>
        </ul>
      )

      showModal({
        title: 'データ確定解除の確認',
        description,
        // 再オープン時に入力・検証エラーを捨てるため、開くたびに本文コンポーネントを新規マウントする
        body: <FixCancelModal key={String(Date.now())} onChange={onChange} />,
        isWide: true,
        isCancel: true,
        cancelName: 'キャンセル',
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            close: false,
            click: async () => {
              const fd = componentRef.formDataRef
              const setErr = componentRef.setErrorsRef
              if (!fd || !setErr) {
                return
              }
              const { ok, errors: vErrors } = validateFixCancelForm(fd)
              if (!ok) {
                setErr(vErrors)
                return
              }
              try {
                await onExecute?.({
                  operationUserSub,
                  operationUserName,
                  permitUserName: String(fd.permitUser).trim(),
                  permitDate: String(fd.permitDate).trim(),
                  key: {
                    CorporationNumber: corporationNumber,
                    Key: dataCreateTaskKey
                  }
                })
                _closeUpModal()
                await onAfterSuccess?.()
                res()
              } catch (e) {
                onFailure?.(e)
              }
            }
          }
        ],
        onClose: res
      })
    })
  }, [
    promisify,
    showModal,
    _closeUpModal,
    globalStore,
    operationUserSub,
    operationUserName,
    corporationNumber,
    dataCreateTaskKey,
    onExecute,
    onAfterSuccess,
    onFailure
  ])

  return { openFixCancelModal }
}
